/**
 * Web-Compatible Password Manager
 * Uses Web Crypto API instead of Electron crypto
 * Handles password encryption and API key storage
 */
export class WebPasswordManager {
    constructor() {
        this.masterPassword = null;
        this.derivedKey = null;
        this.salt = null;
        this.isAuthenticated = false;
        this.userExistsMarker = 'ev_plus_user_exists';
        this.validationMarker = 'ev_plus_password_validation';
    }

    /**
     * Check if user has been set up before
     */
    userExists() {
        return localStorage.getItem(this.userExistsMarker) === 'true';
    }

    /**
     * Set up new user with password
     */
    async setupNewUser(password) {
        try {
            // Generate random salt
            this.salt = crypto.getRandomValues(new Uint8Array(16));
            
            // Derive encryption key from password
            this.derivedKey = await this.deriveKey(password, this.salt);
            
            // Store password in memory only
            this.masterPassword = password;
            this.isAuthenticated = true;

            // Dispatch event for MM4 auto-connect and other listeners
            window.dispatchEvent(new Event('passwordUnlocked'));

            // Create validation data for future logins
            await this.createValidationData();
            
            // Mark user as existing
            localStorage.setItem(this.userExistsMarker, 'true');
            
            // console.log('✅ New user setup complete');
            return true;
            
        } catch (error) {
            // console.error('❌ Failed to setup new user:', error);
            return false;
        }
    }

    /**
     * Authenticate existing user with password
     */
    async authenticateUser(password) {
        try {
            const validationData = localStorage.getItem(this.validationMarker);
            if (!validationData) {
                // console.error('No validation data found');
                return false;
            }

            const validation = JSON.parse(validationData);
            
            // Derive key using stored salt
            const testKey = await this.deriveKey(password, new Uint8Array(validation.salt));
            
            // Try to decrypt validation data
            try {
                const decrypted = await this.decrypt(
                    new Uint8Array(validation.encrypted),
                    testKey,
                    new Uint8Array(validation.iv)
                );
                
                if (decrypted === 'valid_password') {
                    // Password is correct
                    this.masterPassword = password;
                    this.derivedKey = testKey;
                    this.salt = new Uint8Array(validation.salt);
                    this.isAuthenticated = true;

                    // console.log('✅ User authentication successful');

                    // Dispatch event for MM4 auto-connect and other listeners
                    window.dispatchEvent(new Event('passwordUnlocked'));

                    return true;
                }
            } catch (decryptError) {
                // console.error('Invalid password');
                return false;
            }
            
            return false;
            
        } catch (error) {
            // console.error('❌ Authentication failed:', error);
            return false;
        }
    }

    /**
     * Create encrypted validation data for password verification
     */
    async createValidationData() {
        try {
            // Encrypt a known string to validate password later
            const validationString = 'valid_password';
            const iv = crypto.getRandomValues(new Uint8Array(12));
            
            const encrypted = await this.encrypt(validationString, this.derivedKey, iv);
            
            // Store validation data - convert ArrayBuffer to Array
            localStorage.setItem(this.validationMarker, JSON.stringify({
                encrypted: Array.from(new Uint8Array(encrypted)),
                iv: Array.from(iv),
                salt: Array.from(this.salt)
            }));
            
        } catch (error) {
            // console.error('Failed to create validation data:', error);
            throw error;
        }
    }

    /**
     * Derive encryption key from password using PBKDF2
     */
    async deriveKey(password, salt) {
        const enc = new TextEncoder();
        
        // Import password as key material
        const passwordKey = await crypto.subtle.importKey(
            'raw',
            enc.encode(password),
            'PBKDF2',
            false,
            ['deriveBits', 'deriveKey']
        );
        
        // Derive AES key
        return await crypto.subtle.deriveKey(
            {
                name: 'PBKDF2',
                salt,
                iterations: 100000,
                hash: 'SHA-256'
            },
            passwordKey,
            { name: 'AES-GCM', length: 256 },
            true,
            ['encrypt', 'decrypt']
        );
    }

    /**
     * Encrypt data using AES-GCM
     */
    async encrypt(data, key, iv) {
        const enc = new TextEncoder();
        const encoded = enc.encode(data);
        
        return await crypto.subtle.encrypt(
            { name: 'AES-GCM', iv },
            key,
            encoded
        );
    }

    /**
     * Decrypt data using AES-GCM
     */
    async decrypt(encrypted, key, iv) {
        const decrypted = await crypto.subtle.decrypt(
            { name: 'AES-GCM', iv },
            key,
            encrypted
        );
        
        const dec = new TextDecoder();
        return dec.decode(decrypted);
    }

    /**
     * Encrypt and store API key and public address
     */
    async encryptApiKey(apiKey, publicAddress = null) {
        if (!this.isAuthenticated || !this.derivedKey) {
            throw new Error('User not authenticated');
        }

        // console.log('🔐 encryptApiKey called with:', {
        //     apiKey: apiKey ? 'PROVIDED' : 'MISSING',
        //     publicAddress: publicAddress,
        //     publicAddressType: typeof publicAddress
        // });

        try {
            const iv = crypto.getRandomValues(new Uint8Array(12));
            const encrypted = await this.encrypt(apiKey, this.derivedKey, iv);
            
            // Store encrypted API key - convert ArrayBuffer to Array
            localStorage.setItem('ev_plus_encrypted_api_key', JSON.stringify({
                encrypted: Array.from(new Uint8Array(encrypted)),
                iv: Array.from(iv)
            }));
            
            // Also encrypt and store public address if provided
            if (publicAddress && publicAddress.trim()) {
                const addressIv = crypto.getRandomValues(new Uint8Array(12));
                const encryptedAddress = await this.encrypt(publicAddress, this.derivedKey, addressIv);
                
                localStorage.setItem('ev_plus_encrypted_address', JSON.stringify({
                    encrypted: Array.from(new Uint8Array(encryptedAddress)),
                    iv: Array.from(addressIv)
                }));
                // console.log('✅ Public address encrypted and stored');
            } else {
                // console.log('⚠️ No public address provided to save');
            }
            
            // console.log('✅ API key and address encrypted and stored');
            return true;
            
        } catch (error) {
            // console.error('❌ Failed to encrypt API key:', error);
            return false;
        }
    }

    /**
     * Decrypt API key and public address from storage
     */
    async decryptApiKey() {
        if (!this.isAuthenticated || !this.derivedKey) {
            throw new Error('User not authenticated');
        }

        try {
            const storedData = localStorage.getItem('ev_plus_encrypted_api_key');
            if (!storedData) {
                throw new Error('No API key stored');
            }

            const data = JSON.parse(storedData);
            const decrypted = await this.decrypt(
                new Uint8Array(data.encrypted),
                this.derivedKey,
                new Uint8Array(data.iv)
            );
            
            // Also decrypt public address if stored
            let publicAddress = null;
            const storedAddress = localStorage.getItem('ev_plus_encrypted_address');
            if (storedAddress) {
                const addressData = JSON.parse(storedAddress);
                publicAddress = await this.decrypt(
                    new Uint8Array(addressData.encrypted),
                    this.derivedKey,
                    new Uint8Array(addressData.iv)
                );
            }
            
            return { apiKey: decrypted, publicAddress };
            
        } catch (error) {
            // console.error('❌ Failed to decrypt API key:', error);
            throw error;
        }
    }

    /**
     * Check if API key is stored
     */
    hasStoredApiKey() {
        return localStorage.getItem('ev_plus_encrypted_api_key') !== null;
    }

    /**
     * Clear all stored data (logout)
     */
    clearAllData() {
        this.masterPassword = null;
        this.derivedKey = null;
        this.salt = null;
        this.isAuthenticated = false;
        
        localStorage.removeItem(this.userExistsMarker);
        localStorage.removeItem(this.validationMarker);
        localStorage.removeItem('ev_plus_encrypted_api_key');
        localStorage.removeItem('ev_plus_encrypted_address');
        
        // console.log('✅ All password data cleared');
    }

    /**
     * Reset user (alias for clearAllData)
     */
    resetUser() {
        this.clearAllData();
        return true;
    }

    /**
     * Check authentication status
     */
    isUserAuthenticated() {
        return this.isAuthenticated;
    }
}

// Create singleton instance
if (typeof window !== 'undefined') {
    window.WebPasswordManager = new WebPasswordManager();
}

export default WebPasswordManager;
